<template>
  <b-container id="app" :fluid="true" class="h-100">
    <b-row align-v="stretch" class="h-100">
      <b-col cols="12" md="6" class="p-0 d-flex flex-column">
        <b-row class="main-content mx-0">
          <b-col class="p-0">
            <div class="logo clickable">
              <b-img
                @click="goToHome"
                width="107"
                height="65"
                :src="`${publicPath}logo.svg`"
              ></b-img>
            </div>
            <router-view />
          </b-col>
        </b-row>
        <b-row class="footer mx-0 flex-shrink-0">
          <b-col class="text-center">
            <div class="copyright">
              Copyright © {{vue_app_year}}
              <a href="https://petroleum.digital/" target="_blank"
                >Digital Petroleum</a
              >. {{ $t("rights") }}.
<!--              {{ $t("version") }}:-->
<!--              {{vue_app_version}}.-->
            </div>
            <div class="copyright">
              <a :href="$t('terms-and-conditions-url')" target="_blank">
                {{ $t("terms-and-conditions") }}
              </a>
              <a :href="$t('privacy-policy-url')" target="_blank">{{
                $t("privacy-policy")
              }}</a>
            </div>
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="12" md="6" class="splash-screen d-md-block d-none">
        <b-img src="@/assets/img/top-corner.svg" class="top-corner"></b-img>
        <b-img
          src="@/assets/img/bottom-corner.svg"
          class="bottom-corner"
        ></b-img>
        <h3 class="app-name">
          {{
            applicationName
              ? applicationName
              : $t("default-application-full-name")
          }}
        </h3>
        <div
          v-if="applicationDescription"
          class="app-description"
          v-html="applicationDescription"
        ></div>
        <div
          v-else
          class="app-description"
          v-html="$t('default-application-description')"
        ></div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import i18n from '@/plugins/i18n'
import {localize} from 'vee-validate'
import ClientService from '@/services/client-service'

export default {
  name: 'App',
  data() {
    return {
      publicPath: process.env.BASE_URL,
      application: {
        name: {
          ru: null,
          en: null
        },
        description: {
          ru: null,
          en: null
        }
      },
      vue_app_year: process.env.VUE_APP_YEAR,
      vue_app_version: process.env.VUE_APP_UI_VERSION
    }
  },
  computed: {
    applicationName() {
      return this.$store.getters.getApplication.fullName
    },
    applicationDescription() {
      return this.$store.getters.getApplication.description
    }
  },
  beforeCreate() {
    const locale = navigator.language.split('-')[0]

    // activate i18n locale
    i18n.locale = locale
    // activate vee-validate locale
    localize(locale)

    this.$log.info('Current browser locale: ' + locale)
  },
  mounted() {
    this.setupClientInfo()
    this.validateAndLoadClientInfo()
  },
  methods: {
    goToHome() {
      this.$router
          .push({
            name: 'home'
          })
          .catch((error) => {
            if (
              error.name !== 'NavigationDuplicated' &&
            !error.message.includes(
                'Avoided redundant navigation to current location'
            )
            ) {
              this.$log.error(error)
            }
          })
    },
    setupClientInfo() {
      const query = this.$route.query
      if (Object.keys(query).length > 0) {
        const copy = Object.assign({}, this.$store.getters.getClient)
        if (query.client_id) {
          copy.clientId = query.client_id
        }
        if (query.redirect_url) {
          copy.redirectUrl = query.redirect_url
        }
        this.$store.dispatch('setClient', copy)
        this.$router.replace({
          ...this.$route,
          query: {}
        })
      }
    },
    validateAndLoadClientInfo() {
      // const client = this.$store.getters.getClient
      ClientService.checkClient()
          .then((res) => {
            ClientService.description(res.data.client_id, i18n.locale).then(
                (result) => {
                  const application = {
                    clientId: res.data.client_id,
                    fullName: result.data.full_name,
                    description: result.data.description
                  }
                  this.$store.dispatch('setApplication', application)
                }
            )
          })
          .catch(() => {
            this.$store.dispatch('setClient', {
              clientId: null,
              redirectUrl: null
            })
            this.$store.dispatch('setApplication', {
              clientId: null,
              fullName: null,
              description: null
            })
          })
    }
  }
}
</script>

<style lang="scss">
@include media-breakpoint-up(lg) {
  .main-content {
    padding: 100px 68px 0 110px !important;

    .logo {
      padding-bottom: 80px;
    }
  }
}

@include media-breakpoint-down(lg) {
  .main-content {
    padding: 75px 40px 0 82px !important;

    .logo {
      padding-bottom: 40px;
    }
  }
}

@include media-breakpoint-down(md) {
  .main-content {
    padding: 50px 40px 0 55px !important;

    .logo {
      padding-bottom: 40px;
    }
  }
}

.main-content {
  flex: 1 0 auto;
}

.footer {
  width: 100%;
  padding-bottom: 28px;
  padding-top: 28px;

  .copyright {
    margin-top: 8px;

    a:not(:first-child) {
      margin-left: 16px;
    }
  }
}

.splash-screen {
  background: linear-gradient(
    90deg,
    #b13491 -34.77%,
    #a73f99 -23.56%,
    #8368b4 20.86%,
    #6788c9 64.41%,
    #529fd9 106.35%,
    #46ace2 145.98%,
    #42b1e5 180.84%
  );
  padding: 0 !important;

  .top-corner {
    position: absolute;
    top: 0;
    right: 0;
  }

  .bottom-corner {
    position: absolute;
    bottom: 0;
    left: 0;
  }

  .app-name {
    color: $--color-base-01;
    padding: 100px 32px 0;
  }

  .app-description {
    color: $--color-base-01;
    padding: 16px 32px 140px;
  }
}
</style>

<i18n>
{
    "en": {
        "default-application-full-name": "Digital Petroleum",
        "default-application-description": "<p>User authentication service</p>",
        "rights": "All rights reserved",
        "terms-and-conditions": "Terms and Conditions",
        "terms-and-conditions-url": "https://petroleum.digital/offer/en.html",
        "privacy-policy": "Privacy Policy",
        "privacy-policy-url": "https://petroleum.digital/privacy-policy/en.html",
        "version": "UI version"
    },
    "ru": {
        "default-application-full-name": "Digital Petroleum",
        "default-application-description": "<p>Сервис аутентификации пользователей</p>",
        "rights": "Все права защищены",
        "terms-and-conditions": "Условия использования",
        "terms-and-conditions-url": "https://petroleum.digital/offer/ru.html",
        "privacy-policy": "Конфиденциальность",
        "privacy-policy-url": "https://petroleum.digital/privacy-policy/ru.html",
        "version": "Версия UI"
    }
}
</i18n>
